import { useMutation, useQuery } from '@apollo/client';
import { setNotificationConfig, setNotificationDisabled, setNotificationItems, } from '@innovyze/stylovyze/lib/actions';
import { Button } from '@innovyze/stylovyze/lib/components';
import { Svg } from '@innovyze/stylovyze/lib/components/Svg';
import { useGlobalization } from '@innovyze/stylovyze/lib/contexts';
import { useCustomSubscription } from '@innovyze/stylovyze/lib/hooks/';
import { useSelectNotificationHub } from '@innovyze/stylovyze/lib/selectors';
import { NotificationStatus, } from '@innovyze/stylovyze/lib/types';
import { rem, useIsFeatureEnabled } from '@innovyze/stylovyze/lib/utils';
import { Grid } from '@mui/material';
import Timestamp from '@weave-design/timestamp';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Link } from 'react-router-dom';
import { NoNewNotifications } from './NotificationManagement/NoNewNotifications.component';
import { NotificationDisabledSwitch } from './NotificationManagement/NotificationDisabledSwitch.component';
import { getParsedMetaData } from './NotificationManagement/utils';
import { StyledLink, StyledNotificationText, StyledNotificationsFlyout, StyledNotificationsFlyoutWithToggle, } from './StylovyzeNotification.styles';
import UnreadIndicator from './UnreadIndicator.svg';
import { ARCHIVE_NOTIFICATIONS, DISMISS_NOTIFICATIONS, MARK_NOTIFICATIONS_AS_VIEWED, TOGGLE_NOTIFICATIONS, } from './graphql/mutations';
import { GET_NOTIFICATION_SETTING, QUERY_NOTIFICATIONS, } from './graphql/queries';
import { ON_CREATE_NOTIFICATION } from './graphql/subscriptions';
import { getNotificationIcon, getNotificationType, isActive, isViewed, } from './utils';
const StylovyzeNotification = ({ config: { applicationId, tenantId, userId, product }, }) => {
    const MAX_NOTIFICATIONS_SHOWN = 15;
    const { t } = useGlobalization();
    const dispatch = useDispatch();
    const isNotificationManagementEnabled = !!useIsFeatureEnabled('info-360-notification-management');
    const isNotificationToggleEnabled = !!useIsFeatureEnabled('info-360-notification-toggle');
    const isDeleteNotificationEnabled = !!useIsFeatureEnabled('info-360-delete-notification');
    const [isOpen, setIsOpen] = useState(false);
    const [unreadCount, setUnreadCount] = useState(0);
    const [notifications, setNotifications] = useState([]);
    const { notificationItems, config: { notificationDisabled }, } = useSelectNotificationHub();
    const [dismissIds, setDismissIds] = useState([]);
    const [markNotificationsAsViewed] = useMutation(MARK_NOTIFICATIONS_AS_VIEWED);
    const [toggleNotifications] = useMutation(TOGGLE_NOTIFICATIONS);
    const [archiveNotifications] = useMutation(ARCHIVE_NOTIFICATIONS);
    const [dismissNotifications] = useMutation(DISMISS_NOTIFICATIONS);
    const { data, loading } = useQuery(QUERY_NOTIFICATIONS, {
        variables: {
            tenantId,
            userId,
            product,
            status: isDeleteNotificationEnabled
                ? [
                    NotificationStatus.ACTIVE,
                    NotificationStatus.VIEWED,
                    NotificationStatus.ARCHIVED,
                    NotificationStatus.DISMISSED,
                ]
                : [NotificationStatus.ACTIVE, NotificationStatus.VIEWED],
        },
    });
    const { data: notificationSettingData, loading: loadingNotificationSetting, error: loadingNotificationError, } = useQuery(GET_NOTIFICATION_SETTING, {
        variables: {
            params: {
                applicationId,
                tenantId,
                userId,
            },
        },
    });
    const { data: createdNotification } = useCustomSubscription(ON_CREATE_NOTIFICATION, {
        variables: { tenantId, product, userId },
    });
    useEffect(() => {
        dispatch(setNotificationConfig({ applicationId, tenantId, userId, product }));
    }, []);
    useEffect(() => {
        if (!data || notificationItems.length > 0) {
            return;
        }
        if (data?.QueryNotifications?.items) {
            dispatch(setNotificationItems(data?.QueryNotifications?.items));
        }
    }, [data]);
    useEffect(() => {
        if (loadingNotificationSetting || notificationDisabled !== undefined)
            return;
        let newNotificationDisabled = notificationSettingData?.GetNotificationSetting
            ?.notificationDisabled;
        if (newNotificationDisabled === undefined) {
            // can't find the user setting, save default value (false) to the BE
            newNotificationDisabled = false;
            toggleNotifications({
                variables: {
                    params: {
                        applicationId,
                        tenantId,
                        userId,
                        notificationDisabled: newNotificationDisabled,
                    },
                },
            });
        }
        dispatch(setNotificationDisabled(newNotificationDisabled));
    }, [
        notificationSettingData,
        loadingNotificationSetting,
        loadingNotificationError,
    ]);
    useEffect(() => {
        if (!createdNotification?.subscribeToNewNotifications) {
            return;
        }
        const addedNotification = JSON.parse(createdNotification.subscribeToNewNotifications.notificationBody);
        dispatch(setNotificationItems([addedNotification, ...notificationItems]));
    }, [createdNotification?.subscribeToNewNotifications]);
    const getNotificationItemsShown = (notificationItems) => notificationItems
        .filter(({ status }) => isActive(status) || isViewed(status))
        .slice(0, MAX_NOTIFICATIONS_SHOWN);
    useEffect(() => {
        const handleDismiss = (id) => {
            try {
                const options = {
                    variables: {
                        params: { product, tenantId, userId, ids: [id] },
                    },
                };
                if (isDeleteNotificationEnabled) {
                    dismissNotifications(options);
                }
                else {
                    archiveNotifications(options);
                }
                setDismissIds(prevDismissIds => prevDismissIds.concat([id]));
            }
            catch (e) {
                console.error('Failed to dismiss notification', e);
            }
        };
        const notificationItemsShown = getNotificationItemsShown(notificationItems).map(({ id, notificationText, timestamp, status, severityLevel, metaData, }) => {
            const { link, linkText } = getParsedMetaData(metaData);
            return {
                id,
                unread: true,
                featured: true,
                type: getNotificationType(severityLevel),
                image: getNotificationIcon(severityLevel),
                showDismissButton: true,
                dismissButtonTitle: t('Dismiss'),
                onDismiss: () => handleDismiss(id),
                timestamp: (React.createElement(Grid, { container: true, spacing: 1 },
                    isActive(status) && (React.createElement(Grid, { item: true },
                        React.createElement(Svg, { svg: UnreadIndicator }))),
                    React.createElement(Grid, { item: true },
                        React.createElement(Timestamp, { timestamp: new Date(timestamp).toString() })))),
                content: (React.createElement(React.Fragment, null,
                    React.createElement(StyledNotificationText, null, notificationText),
                    link && linkText ? (React.createElement(StyledLink, { to: link, onClick: () => setIsOpen(false) }, linkText)) : null)),
            };
        });
        setUnreadCount(getNotificationItemsShown(notificationItems).filter(({ status }) => isActive(status)).length);
        const handleDismissAll = () => {
            const activeOrViewedIds = notificationItems
                .filter(({ status }) => isActive(status) || isViewed(status))
                .map(({ id }) => id ?? '');
            if (activeOrViewedIds.length === 0) {
                return;
            }
            try {
                const options = {
                    variables: {
                        params: {
                            product,
                            tenantId,
                            userId,
                            ids: activeOrViewedIds,
                        },
                    },
                };
                if (isDeleteNotificationEnabled) {
                    dismissNotifications(options);
                }
                else {
                    archiveNotifications(options);
                }
                setDismissIds(prevDismissIds => prevDismissIds.concat(activeOrViewedIds));
                setNotifications([]);
            }
            catch (e) {
                console.error('Failed to dismiss all notifications', e);
            }
        };
        const dismissAllNotifications = {
            id: 'dismiss-all-notificationItems',
            featured: false,
            showDismissButton: false,
            unread: false,
            content: (React.createElement(React.Fragment, null,
                React.createElement(Button, { dataCy: "dismissAllNotificationsButton", variant: "outlined", color: "secondary", onClick: () => handleDismissAll() }, t('Dismiss all')),
                isNotificationManagementEnabled && (React.createElement(Link, { to: "/notification-management", onClick: () => setIsOpen(false), style: { marginLeft: '16px' } }, t('View all notifications'))))),
        };
        const notificationSwitch = {
            id: 'notification-settings',
            featured: false,
            showDismissButton: false,
            unread: false,
            content: (React.createElement("div", { style: { width: `${rem(261)}` } },
                React.createElement(NotificationDisabledSwitch, null),
                notificationDisabled && React.createElement(NoNewNotifications, null))),
        };
        let newNotifications = [
            ...notificationItemsShown,
            dismissAllNotifications,
        ];
        if (isNotificationToggleEnabled && notificationDisabled !== undefined)
            newNotifications = [notificationSwitch, ...newNotifications];
        setNotifications(newNotifications);
    }, [notificationItems, notificationDisabled, isNotificationToggleEnabled]);
    const setNotificationsAsViewed = () => {
        const unreadIds = getNotificationItemsShown(notificationItems)
            .filter(n => isActive(n.status) && !dismissIds.some(id => id === n.id))
            .map(({ id }) => id);
        if (unreadIds.length === 0) {
            return;
        }
        markNotificationsAsViewed({
            variables: {
                params: {
                    product,
                    tenantId,
                    userId,
                    ids: unreadIds,
                },
            },
        });
        dispatch(setNotificationItems(notificationItems.map(notificationItem => unreadIds.some(id => id === notificationItem.id)
            ? {
                ...notificationItem,
                status: NotificationStatus.VIEWED,
            }
            : notificationItem)));
    };
    const removeDismissedNotificationItems = () => {
        if (dismissIds.length === 0) {
            return;
        }
        const newNotificationItems = isDeleteNotificationEnabled
            ? notificationItems.map(n => dismissIds.some(id => id === n.id)
                ? {
                    ...n,
                    status: NotificationStatus.DISMISSED,
                }
                : n)
            : notificationItems.filter(n => !dismissIds.some(id => id === n.id));
        dispatch(setNotificationItems(newNotificationItems));
        setDismissIds([]);
    };
    useEffect(() => {
        if (!isOpen) {
            setNotificationsAsViewed();
            removeDismissedNotificationItems();
        }
    }, [isOpen]);
    return isNotificationToggleEnabled ? (React.createElement(StyledNotificationsFlyoutWithToggle, { notifications: notifications, onClick: () => setIsOpen(!isOpen), onClickOutside: () => setIsOpen(false), open: isOpen, loading: loading, unreadCount: unreadCount, indicatorTitle: t('Notifications'), heading: t('Notifications') })) : (React.createElement(StyledNotificationsFlyout, { notifications: notifications, onClick: () => setIsOpen(!isOpen), onClickOutside: () => setIsOpen(false), open: isOpen, loading: loading, unreadCount: unreadCount, indicatorTitle: t('Notifications'), heading: t('Notifications') }));
};
export default React.memo(StylovyzeNotification);
